/* eslint-disable max-lines -- canViewRent (CU-86cbgnt3z) pushed the file one line over the 400 cap; extraction out of scope for this narrow fix. */
import * as React from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useNavigate, useRouteContext, useSearch } from '@tanstack/react-router';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { useColumnPreferences } from '@ooh/ui/components/data-table-column-preferences';
import { useTableVirtualizer } from '@ooh/ui/components/data-table-virtualizer';
import {
  mediaUnits,
  campaignFilterPresets,
  PERMISSIONS,
  type MediaUnitSortKey,
  type MediaUnitListResponse,
} from '@ooh/contracts';
import {
  buildFilterFields,
  panelValueToFilters,
  CATALOG_ONLY_ARRAY_FIELD_IDS,
} from '../lib/filter-fields';
import { useFilterLabels } from './use-filter-labels';
import { useLocationFilterPanel } from './use-location-filter-panel';
import { SHARED_FILTER_FIELD_IDS } from '../../../lib/filter-config';
import { useSharedFilters, buildNextSharedFilterSearch } from '../../../hooks/use-shared-filters';
import { usePoiFilterParams } from '../../../hooks/use-poi-filter-params';
// prettier-ignore
import { useSearchParam, useNoPhotoParam, useExcludeParam, useAvailabilityRangeParams, useImportJobIdParam, useRangeFilterParams, buildNextRangeFilterValues } from './use-catalog-sidecar-params';
import {
  buildColumns,
  DEFAULT_HIDDEN_COLUMNS,
  ROW_ESTIMATE_PX,
  OVERSCAN,
  SELECT_COLUMN_ID,
  type MediaUnitColumnDef,
} from '@ooh/screens';
import type { MediaUnitsSearch } from '../lib/validate-search';
import { useMediaBulkSelection } from './use-media-bulk-selection';
import { mediaUnitKeys, filterOptionsQuery, mediaUnitDetailQuery } from '../api/queries';
import { computeNextSort } from '../lib/sort-helpers';
import { poiQueries } from '../../pois/api/queries';
import { buildOrderedVisibleColumns } from '../../../lib/ordered-visible-columns';
import { resolveCanImport } from '../lib/resolve-can-import';

/**
 * Pure merge logic behind handleCombinedFiltersChange's functional search updater:
 * folds a single FilterPanel `value` object (shared filters + catalog-only selects +
 * numeric ranges, all keyed by field id) onto `prev` in one pass. Extracted so the
 * SHARED_FILTER_FIELD_IDS/CATALOG_ONLY_ARRAY_FIELD_IDS wiring is unit-testable without
 * rendering the full page hook (CU-86cataqt9).
 */
export function buildNextCombinedFilterSearch(
  prev: Record<string, unknown>,
  value: Record<string, string[]>,
): Record<string, unknown> {
  let next = buildNextSharedFilterSearch(prev, SHARED_FILTER_FIELD_IDS, value);
  next = buildNextSharedFilterSearch(next, CATALOG_ONLY_ARRAY_FIELD_IDS, value);
  next = buildNextRangeFilterValues(next, value);
  return next;
}

interface UseMediaUnitsPageOptions {
  canQuickAddToOffer?: boolean;
  canViewCosts?: boolean;
  /** Wildcard cost.view_rent grant, narrower than canViewCosts (CU-86cbgnt3z). */
  canViewRent?: boolean;
  canViewSupplierDetails?: boolean;
  /** True when the caller holds `bulk_edit` and/or `media_unit.block_availability`. */
  canBulkEdit?: boolean;
}

// eslint-disable-next-line max-lines-per-function, complexity
export function useMediaUnitsPage(options: UseMediaUnitsPageOptions = {}) {
  const {
    canQuickAddToOffer = false,
    canViewCosts = true,
    canViewRent = true,
    canViewSupplierDetails = true,
    canBulkEdit = false,
  } = options;
  const { auth } = useRouteContext({ from: '__root__' });
  const navigate = useNavigate({ from: '/media-units/' });
  const { t } = useTranslation('inventory');
  const { t: tCommon } = useTranslation('common');
  const { t: tMap } = useTranslation('map');

  // Single shared row-selection affordance for the whole catalog list, covering
  // quick-add-to-offer, bulk-edit-media, and availability-windows bulk actions.
  // There is exactly one checkbox column and one selection state — see
  // `useMediaBulkSelection` below.
  const canSelectRows = canQuickAddToOffer || canBulkEdit;

  const { filterFieldLabels, filterPanelLabels } = useFilterLabels(t, tMap);

  const searchParams = useSearch({ from: '/media-units/' }) as MediaUnitsSearch;
  const campaignId =
    typeof searchParams.campaignId === 'number' ? searchParams.campaignId : undefined;

  const sortBy: MediaUnitSortKey = searchParams.sortBy ?? 'id';
  const sortDir: 'asc' | 'desc' = searchParams.sortDir ?? 'asc';
  const sortBy2: MediaUnitSortKey | undefined = searchParams.sortBy2;
  const sortDir2: 'asc' | 'desc' = searchParams.sortDir2 ?? 'asc';

  const [filterValue, handleFiltersChange] = useSharedFilters(SHARED_FILTER_FIELD_IDS);
  // Catalog-only fields (CU-86cataqt9): material/multiformat/tags/address (array-of-strings
  // storage, same as useSharedFilters) and the 6 numeric ranges (dedicated scalar min/max
  // sidecar pairs — see useRangeFilterParams). Merged into one FilterPanel value/onChange
  // below so the panel only sees a single combined state, like the shared filters.
  const [catalogSelectValue] = useSharedFilters(CATALOG_ONLY_ARRAY_FIELD_IDS);
  const [rangeValue] = useRangeFilterParams();
  const combinedFilterValue = React.useMemo(
    () => ({ ...filterValue, ...catalogSelectValue, ...rangeValue }),
    [filterValue, catalogSelectValue, rangeValue],
  );
  const handleCombinedFiltersChange = React.useCallback(
    (value: Record<string, string[]>) => {
      // Functional search updater: reads LIVE state at commit time instead of a
      // captured snapshot — the same pattern already used by handleSort below.
      // This closes the stale-snapshot clobbering race the previous hand-rolled
      // rawSearch/rawNavigate implementation worked around (see
      // buildNextSharedFilterSearch's doc comment for the underlying race).
      void navigate({
        search: (prev) => buildNextCombinedFilterSearch(prev as Record<string, unknown>, value),
        replace: true,
      });
    },
    [navigate],
  );
  const [poiFilterParams, setPoiFilterParams] = usePoiFilterParams();
  const [searchValue, setSearchValue] = useSearchParam();
  const [noPhoto, setNoPhoto] = useNoPhotoParam();
  const [excludedFields, toggleExcludedField] = useExcludeParam();
  const [availabilityRange, setAvailabilityRange] = useAvailabilityRangeParams();
  const [importJobId, setImportJobId] = useImportJobIdParam();
  const activeFilters = React.useMemo(
    () =>
      panelValueToFilters(combinedFilterValue, poiFilterParams, {
        search: searchValue,
        noPhoto,
        exclude: excludedFields,
        availabilityFrom: availabilityRange[0],
        availabilityTo: availabilityRange[1],
        importJobId,
      }),
    [
      combinedFilterValue,
      poiFilterParams,
      searchValue,
      noPhoto,
      excludedFields,
      availabilityRange,
      importJobId,
    ],
  );
  const activeFilterCount = Object.keys(combinedFilterValue).filter(
    (k) => combinedFilterValue[k]?.length,
  ).length;

  // ─── Bulk-edit selection state ────────────────────────────────
  // filterKey ends "select all N matching" mode on any search/filter/sort change
  // (that mode is a saved search); the id-based selection itself persists through
  // it and is scoped instead by offerContextKey (row-selection spec).
  const bulkEditFilterKey = React.useMemo(
    () => JSON.stringify({ activeFilters, sortBy, sortDir, sortBy2, sortDir2 }),
    [activeFilters, sortBy, sortDir, sortBy2, sortDir2],
  );

  const bulkSelection = useMediaBulkSelection({
    filterKey: bulkEditFilterKey,
    offerContextKey: searchParams.addToOfferId ?? null,
  });

  // Resolve POI display name for per-POI proximity chip label.
  // Only fetched when nearPoiId is set (per-POI mode).
  const { data: nearPoi } = useQuery({
    ...poiQueries.byId(auth, poiFilterParams.nearPoiId ?? ''),
    enabled: Boolean(poiFilterParams.nearPoiId),
  });
  const nearPoiName = poiFilterParams.nearPoiId ? nearPoi?.name : undefined;

  const { data: filterOptionsData, isLoading: isFilterOptionsLoading } = useQuery(
    filterOptionsQuery(auth),
  );

  const filterFields = React.useMemo(
    () =>
      buildFilterFields(filterOptionsData, filterFieldLabels, {
        canViewCosts,
        canViewRent,
        canViewSupplierDetails,
        includeCatalogOnlyFields: true,
      }),
    [filterOptionsData, filterFieldLabels, canViewCosts, canViewRent, canViewSupplierDetails],
  );

  // Combined "Lokalizacja" pill: merges region/county/agglomeration/city into
  // one FilterPanel-facing value and redistributes updates back on change.
  // Fed the COMBINED value/onChange (shared + catalog-only selects + ranges) so
  // non-location updates pass through to every sidecar setter unchanged.
  const { panelFilterValue, handlePanelFiltersChange } = useLocationFilterPanel(
    filterOptionsData,
    filterFieldLabels,
    combinedFilterValue,
    handleCombinedFiltersChange,
  );

  const presetAppliedRef = React.useRef(false);
  const { data: presetData } = useQuery({
    queryKey: mediaUnitKeys.filterPreset(campaignId!),
    queryFn: ({ signal }) =>
      auth.callWithAuth(campaignFilterPresets.get, { id: campaignId! }, { signal }),
    enabled: campaignId != null,
  });

  React.useEffect(() => {
    const hasActiveFilters = SHARED_FILTER_FIELD_IDS.some((key) => {
      const val = filterValue[key];
      return Array.isArray(val) && val.length > 0;
    });
    if (!presetData || presetAppliedRef.current || !presetData.filters || hasActiveFilters) return;
    presetAppliedRef.current = true;
    const presetFilterValue: Record<string, string[]> = {};
    for (const key of SHARED_FILTER_FIELD_IDS) {
      const vals = presetData.filters[key as keyof typeof presetData.filters];
      if (Array.isArray(vals) && vals.length) presetFilterValue[key] = vals;
    }
    handleFiltersChange(presetFilterValue);
  }, [presetData, handleFiltersChange, filterValue]);

  const [showSaveDialog, setShowSaveDialog] = React.useState(false);

  const { mutate: savePreset } = useMutation({
    mutationFn: () =>
      auth.callWithAuth(campaignFilterPresets.upsert, { id: campaignId!, filters: activeFilters }),
    onSuccess: () => {
      setShowSaveDialog(false);
      toast.success(t('campaign.savedToast'));
    },
  });

  const handleSort = React.useCallback(
    (key: string, shiftKey = false) => {
      void navigate({
        search: (prev) => computeNextSort(prev as MediaUnitsSearch, key, shiftKey),
      });
    },
    [navigate],
  );

  const { data, fetchNextPage, isFetchingNextPage, isLoading, isError, error, refetch } =
    useInfiniteQuery<
      MediaUnitListResponse,
      Error,
      { pages: MediaUnitListResponse[] },
      readonly [string, string, ...string[]],
      string | undefined
    >({
      queryKey: mediaUnitKeys.list({ sortBy, sortDir, sortBy2, sortDir2 }, activeFilters),
      queryFn: ({ pageParam, signal }) =>
        auth.callWithAuth(
          mediaUnits.list,
          {
            cursor: pageParam,
            pageSize: 100,
            sortBy,
            sortDir,
            filters: activeFilters,
            ...(sortBy2 != null ? { sortBy2, sortDir2 } : {}),
          },
          { signal },
        ),
      placeholderData: (prev) => prev,
      initialPageParam: undefined,
      getNextPageParam: (lastPage: MediaUnitListResponse) => lastPage.meta.nextCursor ?? undefined,
    });

  const allRows = React.useMemo(
    () => (data?.pages ?? []).flatMap((p: MediaUnitListResponse) => p.data),
    [data],
  );

  // Cumulative cache of every row seen this session (across search/filter/sort
  // changes), keyed by id — a bulk action must resolve fields (e.g. grwId) for a
  // selected unit even after a later search hides it, without a server round-trip
  // (row-selection spec: bulk actions act on the full selection, not just shown
  // rows). Mutating a ref directly during render, gated on `allRows` identity, is
  // the "adjust state during rendering" pattern: no extra re-render, no effect.
  const seenRowsByIdRef = React.useRef<Map<number, MediaUnitListResponse['data'][number]>>(
    new Map(),
  );
  const lastAllRowsRef = React.useRef<typeof allRows | null>(null);
  if (lastAllRowsRef.current !== allRows) {
    lastAllRowsRef.current = allRows;
    for (const row of allRows) seenRowsByIdRef.current.set(row.id, row);
  }

  const totalCount = data?.pages[0]?.meta.totalCount ?? 0;
  const hasNextPage = data?.pages[data.pages.length - 1]?.meta.nextCursor != null;

  const { scrollContainerRef, sentinelRef, virtualItems, totalSize } = useTableVirtualizer({
    rowCount: allRows.length,
    estimateRowHeightPx: ROW_ESTIMATE_PX,
    overscan: OVERSCAN,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  // Bulk-edit selection column helpers. When "select all N matching" (filter-based
  // selection) is active, every loaded row counts as selected for display purposes —
  // otherwise the checkbox column would go blank right after the banner confirmed the
  // selection, and re-clicking the (visually unchecked) header checkbox would silently
  // downgrade the selection from "all N matching" to just the current page.
  const isBulkSelectAllFilterActive = bulkSelection.selectAllFilter != null;
  const bulkAllRowsSelected =
    isBulkSelectAllFilterActive ||
    (allRows.length > 0 && allRows.every((row) => bulkSelection.selectedIds.has(row.id)));
  // Judged only against the shown rows (row-selection spec: "Header checkbox
  // acts on shown rows only") — a selection with zero overlap with `allRows`
  // must render unchecked, not indeterminate, even though the global
  // selection itself is non-empty.
  const bulkSomeRowsSelected =
    !isBulkSelectAllFilterActive &&
    !bulkAllRowsSelected &&
    allRows.some((row) => bulkSelection.selectedIds.has(row.id));

  // ─── Catalog-table photo preview lightbox ─────────────────────
  // Lazy: the unit detail query (which carries `photos`) only fires once a
  // has-photo row's indicator is actually clicked, gated by `enabled` below —
  // never eagerly for every row in the virtualized table.
  const [photoPreviewUnitId, setPhotoPreviewUnitId] = React.useState<number | null>(null);
  const [photoPreviewActiveIndex, setPhotoPreviewActiveIndex] = React.useState(0);

  const {
    data: photoPreviewDetail,
    isLoading: isPhotoPreviewLoading,
    refetch: refetchPhotoPreview,
  } = useQuery({
    ...mediaUnitDetailQuery(auth, photoPreviewUnitId ?? 0),
    enabled: photoPreviewUnitId != null,
  });
  const photoPreviewPhotos = photoPreviewDetail?.data.photos ?? [];

  const openPhotoPreview = React.useCallback(
    (unitId: number) => {
      // Clicking the SAME still-selected unit again (e.g. retrying after a
      // failed fetch) is otherwise a no-op state set — React bails out on an
      // identical value — so force a fresh fetch attempt explicitly instead.
      if (unitId === photoPreviewUnitId) return void refetchPhotoPreview();
      setPhotoPreviewActiveIndex(0);
      setPhotoPreviewUnitId(unitId);
    },
    [photoPreviewUnitId, refetchPhotoPreview],
  );
  const closePhotoPreview = React.useCallback(() => setPhotoPreviewUnitId(null), []);

  const columns = React.useMemo(
    () =>
      buildColumns(t, tCommon, {
        // Single shared selection column for every bulk-capable permission holder —
        // quick-add-to-offer, bulk-edit-media, and availability-windows block-availability
        // all read and write the same `bulkSelection` state (see useMediaBulkSelection).
        selection: canSelectRows
          ? {
              selectedIds: isBulkSelectAllFilterActive
                ? new Set(allRows.map((r) => r.id))
                : bulkSelection.selectedIds,
              allRowsSelected: bulkAllRowsSelected,
              someRowsSelected: bulkSomeRowsSelected,
              onToggleAll: () =>
                isBulkSelectAllFilterActive
                  ? bulkSelection.clearSelection()
                  : bulkSelection.toggleAll(allRows.map((r) => r.id)),
              // Filter-based selection has no way to exclude a single id (the
              // selection schema is `{ ids } | { filter }`, never both). Per-row
              // toggling is a no-op while it's active; "Wyczyść zaznaczenie" on the
              // toolbar/banner is the only way out, so a row click can never silently
              // collapse "all N matching" down to just that one row.
              onToggleRow: isBulkSelectAllFilterActive ? () => {} : bulkSelection.toggleId,
              headerAriaLabel: t('quickAdd.selection.selectAllAriaLabel'),
              getRowAriaLabel: (item) =>
                t('quickAdd.selection.selectRowAriaLabel', {
                  id: item.grwId ?? item.supplierNumber ?? item.id,
                  city: item.city,
                }),
            }
          : undefined,
        canViewCosts,
        canViewSupplierDetails,
        tMap,
        onOpenPhotoPreview: openPhotoPreview,
        photoPreviewLoadingUnitId: isPhotoPreviewLoading ? photoPreviewUnitId : null,
      }),
    [
      allRows,
      bulkAllRowsSelected,
      bulkSelection,
      bulkSomeRowsSelected,
      canSelectRows,
      canViewCosts,
      canViewSupplierDetails,
      isBulkSelectAllFilterActive,
      isPhotoPreviewLoading,
      openPhotoPreview,
      photoPreviewUnitId,
      t,
      tCommon,
      tMap,
    ],
  );

  const allColumnIds = React.useMemo(() => columns.map((c) => c.id), [columns]);

  const {
    visibleColumnIds,
    hiddenColumnIds,
    toggleColumn,
    columnOrder,
    reorderColumn,
    moveColumnUp,
    moveColumnDown,
    columnWidths,
    setColumnWidth,
  } = useColumnPreferences({
    storageKeyPrefix: 'ooh:inventory',
    allColumnIds,
    defaultHiddenIds: DEFAULT_HIDDEN_COLUMNS,
    // 'select' only exists in `columns` when canSelectRows is true; useColumnPreferences
    // gracefully ignores a pinned id absent from allColumnIds.
    pinnedIds: [SELECT_COLUMN_ID],
  });

  // All role-allowed columns sorted by the user-defined order ('select' pinned first when
  // present). Used by the "Kolumny" picker so its list order matches the table.
  // Role-allowed columns filtered by user-driven visibility, in user-defined order.
  const { orderedColumns, visibleColumns } = React.useMemo(
    () =>
      buildOrderedVisibleColumns<MediaUnitColumnDef>(
        columns,
        columnOrder,
        visibleColumnIds,
        columnWidths,
      ),
    [columns, columnOrder, visibleColumnIds, columnWidths],
  );

  const canImport = resolveCanImport(
    auth.permissions?.includes(PERMISSIONS.IMPORT) ?? false,
    searchParams.addToOfferId,
  );

  return {
    t,
    tCommon,
    allRows,
    /** Every row seen this session across search/filter/sort changes, keyed by id. */
    seenRowsById: seenRowsByIdRef.current,
    totalCount,
    isLoading,
    canImport,
    isError,
    error,
    refetch,
    isFetchingNextPage,
    filterFields,
    filterValue: panelFilterValue,
    handleFiltersChange: handlePanelFiltersChange,
    isFilterOptionsLoading,
    filterPanelLabels,
    activeFilterCount,
    poiFilterParams,
    setPoiFilterParams,
    nearPoiName,
    // Catalog sidecar filters
    searchValue,
    setSearchValue,
    noPhoto,
    setNoPhoto,
    excludedFields,
    toggleExcludedField,
    availabilityRange,
    setAvailabilityRange,
    importJobId,
    setImportJobId,
    campaignId,
    showSaveDialog,
    setShowSaveDialog,
    savePreset,
    sortBy,
    sortDir,
    sortBy2,
    sortDir2,
    handleSort,
    /** All role-allowed columns (use for the column-visibility picker menu). */
    columns,
    /** All role-allowed columns sorted by user-defined order (use for the visibility picker). */
    orderedColumns,
    /** Role-allowed columns filtered by user-driven visibility (use for the actual table render). */
    visibleColumns,
    /** Toggle visibility of a column by id. */
    toggleColumn,
    /** Column ids currently hidden by the user. */
    hiddenColumnIds,
    /** User-defined column order (includes 'select' first when present). */
    columnOrder,
    /** Move a column to the position of another by drag-and-drop. */
    reorderColumn,
    /** Persist a resized catalog column width. */
    setColumnWidth,
    /** Move a column one step up (earlier) in the order. */
    moveColumnUp,
    /** Move a column one step down (later) in the order. */
    moveColumnDown,
    /** True when the caller holds at least one bulk-capable permission (checkbox column visible). */
    canSelectRows,
    scrollContainerRef,
    sentinelRef,
    virtualItems,
    totalSize,
    // Single shared bulk-selection state (quick-add-to-offer, bulk-edit-media,
    // availability-windows block-availability).
    bulkSelectedIds: bulkSelection.selectedIds,
    bulkSelectedCount: bulkSelection.selectedIds.size,
    bulkSelectAllFilter: bulkSelection.selectAllFilter,
    bulkSomeSelected: bulkSelection.someSelected,
    bulkClearSelection: bulkSelection.clearSelection,
    bulkActivateSelectAllFilter: () => bulkSelection.activateSelectAllFilter(activeFilters),
    activeFilters,
    // Catalog-table photo preview lightbox (has-photo indicator click).
    photoPreviewUnitId,
    photoPreviewPhotos,
    photoPreviewActiveIndex,
    setPhotoPreviewActiveIndex,
    closePhotoPreview,
    isPhotoPreviewLoading,
  };
}
